import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Pencil, Plus, Wallet, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useUser } from '../contexts/UserContext';
import NewtonsCradleLoader from '../components/common/NewtonsCradleLoader';
import apiService from '../utils/apiService';
import SubPaymentMethodGrid from '../components/withdrawal/SubPaymentMethodGrid';
import WalletMethodIcon from '../components/withdrawal/WalletMethodIcon';
import {
  extractWalletsList,
  isBankWallet,
  isLocalBankWallet,
  mapWalletToMethod,
  walletNeedsSubPaymentMethods,
} from '../utils/withdrawalWallets';
import {
  resolveSubPaymentMethods,
  subMethodNeedsAddress,
} from '../utils/subPaymentMethods';
import {
  buildPartnerPaymentDetailPayload,
  extractPartnerPaymentDetailsList,
  mapPartnerPaymentDetail,
} from '../utils/partnerPaymentDetails';
import { getThemeColor } from '../config/appConfig';
import { resolveWalletIconUrl } from '../utils/walletIcons';

const THEME = getThemeColor('primary');

const EMPTY_FORM = {
  accountTitle: '',
  bankName: '',
  accountNumber: '',
  confirmAccountNumber: '',
  iban: '',
  cryptoAddress: '',
};

const getLoggedInUserName = (userData) => {
  if (userData?.firstName) {
    return `${userData.firstName} ${userData.lastName || ''}`.trim();
  }
  if (userData?.fullName) return userData.fullName;
  if (userData?.userName) return userData.userName;
  if (userData?.name) return userData.name;
  const storedName = localStorage.getItem('userName');
  if (storedName?.trim()) return storedName.trim();
  return localStorage.getItem('userEmail')?.split('@')[0] || '';
};

const AddWithdrawalDetails = () => {
  const { isDarkMode } = useTheme();
  const { userData } = useUser();
  const [wallets, setWallets] = useState([]);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [isLoadingWallets, setIsLoadingWallets] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [selectedWalletId, setSelectedWalletId] = useState('');
  const [selectedSubPaymentMethodId, setSelectedSubPaymentMethodId] = useState('');
  const [subPaymentMethods, setSubPaymentMethods] = useState([]);
  const [isLoadingSubMethods, setIsLoadingSubMethods] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const walletsFetched = useRef(false);
  const accountsFetched = useRef(false);

  const selectedWallet = wallets.find((w) => w.id === selectedWalletId);
  const selectedSubPaymentMethod = subPaymentMethods.find((m) => m.id === selectedSubPaymentMethodId);
  const hasSubPaymentMethods = subPaymentMethods.length > 0;
  const isBank = isBankWallet(selectedWallet);
  const isLocalBank = isLocalBankWallet(selectedWallet);
  const needsCryptoAddress = !isBank && (
    hasSubPaymentMethods
      ? subMethodNeedsAddress(selectedSubPaymentMethod)
      : true
  );

  const card = isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100';
  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full rounded-xl border px-4 py-3 text-sm outline-none transition focus:border-red-800 focus:ring-2 focus:ring-red-800/15 ${
    isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
  }`;
  const disabledInputCls = `w-full rounded-xl border px-4 py-3 text-sm cursor-not-allowed select-none ${
    isDarkMode
      ? '!bg-gray-800/60 !border-gray-600 !text-gray-400'
      : '!bg-gray-100 !border-gray-300 !text-gray-600'
  }`;

  const loadSavedAccounts = async () => {
    setIsLoadingAccounts(true);
    setLoadError('');
    try {
      const response = await apiService.getPartnerPaymentDetails();
      if (response?.success) {
        setSavedAccounts(
          extractPartnerPaymentDetailsList(response).map(mapPartnerPaymentDetail)
        );
      } else {
        setSavedAccounts([]);
        setLoadError(response?.message || response?.error || 'Unable to load withdrawal details.');
      }
    } catch {
      setSavedAccounts([]);
      setLoadError('Unable to load withdrawal details. Please try again.');
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  useEffect(() => {
    if (accountsFetched.current) return;
    accountsFetched.current = true;
    localStorage.removeItem('withdrawalDetailsList');
    localStorage.removeItem('withdrawalDetails');
    loadSavedAccounts();
  }, []);

  useEffect(() => {
    if (walletsFetched.current) return;
    walletsFetched.current = true;

    const loadWallets = async () => {
      setIsLoadingWallets(true);
      try {
        const response = await apiService.getWallets();
        const list = extractWalletsList(response).map(mapWalletToMethod);
        setWallets(list);
      } catch {
        setWallets([]);
      } finally {
        setIsLoadingWallets(false);
      }
    };

    loadWallets();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSubPaymentMethods = async () => {
      if (!showForm || !selectedWallet?.walletId) {
        setSubPaymentMethods([]);
        setSelectedSubPaymentMethodId('');
        return;
      }

      const embedded = selectedWallet.subPaymentMethods || [];
      if (embedded.length > 0) {
        setSubPaymentMethods(embedded);
        setSelectedSubPaymentMethodId((prev) =>
          prev && embedded.some((m) => m.id === prev)
            ? prev
            : embedded.find((m) => !m.disabled)?.id || embedded[0]?.id || ''
        );
        return;
      }

      if (!walletNeedsSubPaymentMethods(selectedWallet)) {
        setSubPaymentMethods([]);
        setSelectedSubPaymentMethodId('');
        return;
      }

      setIsLoadingSubMethods(true);
      try {
        const response = await apiService.getSubPaymentMethods(selectedWallet.walletId);
        if (cancelled) return;
        const list = resolveSubPaymentMethods(response);
        setSubPaymentMethods(list);
        setSelectedSubPaymentMethodId((prev) =>
          prev && list.some((m) => m.id === prev)
            ? prev
            : list.find((m) => !m.disabled)?.id || list[0]?.id || ''
        );
      } catch {
        if (!cancelled) {
          const list = resolveSubPaymentMethods({ success: false });
          setSubPaymentMethods(list);
          setSelectedSubPaymentMethodId(list.find((m) => !m.disabled)?.id || list[0]?.id || '');
        }
      } finally {
        if (!cancelled) setIsLoadingSubMethods(false);
      }
    };

    loadSubPaymentMethods();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when wallet identity changes
  }, [showForm, selectedWallet?.id, selectedWallet?.walletId]); // eslint-disable-line react-hooks/exhaustive-deps

  const getDefaultForm = () => ({
    ...EMPTY_FORM,
    accountTitle: getLoggedInUserName(userData),
  });

  const resetForm = () => {
    setForm(getDefaultForm());
    setEditingId('');
    setSelectedWalletId('');
    setSelectedSubPaymentMethodId('');
    setSubPaymentMethods([]);
    setMessage('');
    setMessageType('');
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
    if (wallets.length > 0) {
      const first = wallets.find((w) => !w.disabled) || wallets[0];
      setSelectedWalletId(first.id);
    }
  };

  const openEditForm = (account) => {
    setMessage('');
    setMessageType('');
    setEditingId(account.id);
    setShowForm(true);
    setSelectedSubPaymentMethodId(account.subPaymentMethodId || '');
    setForm({
      accountTitle: getLoggedInUserName(userData),
      bankName: account.bankName || '',
      accountNumber: account.accountNumber || '',
      confirmAccountNumber: account.accountNumber || '',
      iban: account.iban || account.swiftCode || '',
      cryptoAddress: account.cryptoAddress || '',
    });
    const wallet = wallets.find((w) => String(w.walletId) === String(account.walletId));
    if (wallet) setSelectedWalletId(wallet.id);
  };

  useEffect(() => {
    if (!editingId || !showForm || wallets.length === 0) return;
    const account = savedAccounts.find((a) => a.id === editingId);
    if (!account) return;
    const wallet = wallets.find((w) => String(w.walletId) === String(account.walletId));
    if (wallet && wallet.id !== selectedWalletId) setSelectedWalletId(wallet.id);
  }, [editingId, showForm, wallets, savedAccounts, selectedWalletId]);

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSelectWallet = (wallet) => {
    if (wallet.disabled) return;
    setMessage('');
    setMessageType('');
    setSelectedWalletId(wallet.id);
    setSelectedSubPaymentMethodId('');
    if (!editingId) {
      setForm({
        ...EMPTY_FORM,
        accountTitle: getLoggedInUserName(userData),
      });
    }
  };

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validateForm = () => {
    if (!selectedWallet) return 'Please select a withdrawal method';
    if (walletNeedsSubPaymentMethods(selectedWallet) && hasSubPaymentMethods && !selectedSubPaymentMethod) {
      return 'Please select a payment network';
    }
    if (!form.accountTitle.trim()) return 'Account title is required';
    if (isLocalBank) {
      if (!form.bankName.trim()) return 'Bank name is required';
      if (!form.accountNumber.trim()) return 'Account number is required';
      if (!form.confirmAccountNumber.trim()) return 'Please confirm account number';
      if (form.accountNumber.trim() !== form.confirmAccountNumber.trim()) {
        return 'Account numbers do not match';
      }
      return '';
    }
    if (isBank) {
      if (!form.accountNumber.trim()) return 'Account number is required';
      if (!form.bankName.trim()) return 'Bank name is required';
      return '';
    }
    if (needsCryptoAddress && !form.cryptoAddress.trim()) return 'Wallet address is required';
    return '';
  };

  const getApiErrorMessage = (response, fallback) => {
    const apiMessage = (response?.message || response?.error || '').toString().trim();
    if (!apiMessage || /^success$/i.test(apiMessage) || /successfully/i.test(apiMessage)) {
      return fallback;
    }
    return apiMessage;
  };

  const showError = (text) => {
    setMessage(text);
    setMessageType('error');
  };

  const showSuccess = (text) => {
    setMessage(text);
    setMessageType('success');
  };

  const clearMessage = () => {
    setMessage('');
    setMessageType('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessage();
    const error = validateForm();
    if (error) {
      showError(error);
      return;
    }

    setIsSaving(true);
    try {
      const editingAccount = editingId
        ? savedAccounts.find((a) => a.id === editingId)
        : null;
      const payload = buildPartnerPaymentDetailPayload({
        selectedWallet,
        selectedSubPaymentMethod,
        form,
        userPaymentDetailId: editingAccount?.userPaymentDetailId ?? editingAccount?.partnerPaymentDetailId ?? null,
      });

      const response = editingId
        ? await apiService.updatePartnerPaymentDetails(payload)
        : await apiService.createPartnerPaymentDetails(payload);

      if (!response?.success) {
        showError(getApiErrorMessage(response, 'Failed to save withdrawal details'));
        return;
      }

      await loadSavedAccounts();
      showSuccess(
        editingId ? 'Withdrawal details updated successfully.' : 'Withdrawal details added successfully.'
      );
      setTimeout(() => clearMessage(), 3000);
      closeForm();
    } catch {
      showError('Failed to save withdrawal details. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const displayVal = (val) => (val?.toString().trim() ? val : '—');

  const renderMessageBanner = (className = 'mb-4', type = messageType) => {
    if (!message || !type) return null;
    const isError = type === 'error';
    return (
      <div
        className={`flex items-center gap-2 text-sm rounded-xl px-4 py-3 ${className} ${
          isError
            ? isDarkMode ? 'bg-red-950 text-red-300' : 'bg-red-50 text-red-700'
            : isDarkMode ? 'bg-emerald-950 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
        }`}
      >
        {!isError ? <Check className="w-4 h-4 shrink-0" /> : null}
        {message}
      </div>
    );
  };

  return (
    <div className="min-h-full bg-[#F4F6F9]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${text}`}>Withdrawal Details</h1>
            <p className={`text-sm mt-1 ${muted}`}>
              <Link to="/" className="hover:text-red-800">Dashboard</Link>
              <span className="mx-1.5">›</span>
              <span>Settings</span>
              <span className="mx-1.5">›</span>
              <span>Withdrawal Details</span>
            </p>
          </div>
          {!showForm && (
            <button
              type="button"
              onClick={openAddForm}
              disabled={isLoadingWallets}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: THEME }}
            >
              <Plus className="w-4 h-4" />
              Add Account
            </button>
          )}
        </div>

        {message && !showForm && renderMessageBanner()}

        {showForm ? (
          <form onSubmit={handleSubmit} noValidate className={`rounded-2xl border shadow-sm p-5 sm:p-6 space-y-6 ${card}`}>
            <div className="flex items-center justify-between gap-3">
              <h2 className={`text-sm font-semibold ${text}`}>
                {editingId ? 'Edit Withdrawal Account' : 'Add Withdrawal Account'}
              </h2>
              <button
                type="button"
                onClick={closeForm}
                className={`p-2 rounded-lg transition ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                aria-label="Close form"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {messageType === 'error' && renderMessageBanner()}

            <div>
              <h3 className={`text-xs font-semibold uppercase tracking-wide mb-3 ${muted}`}>Withdrawal Method</h3>
              {isLoadingWallets ? (
                <div className="flex justify-center py-10">
                  <NewtonsCradleLoader />
                </div>
              ) : wallets.length === 0 ? (
                <p className={`text-sm text-center py-8 ${muted}`}>No withdrawal methods available.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {wallets.map((wallet) => {
                    const active = selectedWalletId === wallet.id;
                    return (
                      <button
                        key={wallet.id}
                        type="button"
                        disabled={wallet.disabled}
                        onClick={() => handleSelectWallet(wallet)}
                        className={`rounded-xl border p-4 text-left transition ${
                          wallet.disabled
                            ? 'opacity-50 cursor-not-allowed'
                            : active
                              ? 'border-red-800 bg-red-50/80'
                              : isDarkMode
                                ? 'border-gray-700 hover:bg-gray-800'
                                : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <WalletMethodIcon
                          iconUrl={wallet.iconUrl}
                          methodKey={wallet.methodKey}
                          imgClassName="w-8 h-8 mb-2 object-contain"
                          iconClassName={`w-5 h-5 mb-2 ${active ? 'text-red-800' : muted}`}
                        />
                        <p className={`text-sm font-medium ${text}`}>{wallet.title}</p>
                        {wallet.subtitle && (
                          <p className={`text-[11px] mt-0.5 ${muted}`}>{wallet.subtitle}</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {selectedWallet && walletNeedsSubPaymentMethods(selectedWallet) && (hasSubPaymentMethods || isLoadingSubMethods) ? (
                <div className="mt-4">
                  <SubPaymentMethodGrid
                    methods={subPaymentMethods}
                    selectedId={selectedSubPaymentMethodId}
                    onSelect={(method) => {
                      clearMessage();
                      setSelectedSubPaymentMethodId(method.id);
                    }}
                    isLoading={isLoadingSubMethods}
                    isDarkMode={isDarkMode}
                    title="Select Payment Network"
                  />
                </div>
              ) : null}
            </div>

            {selectedWallet && (
              <div className="space-y-4">
                <h3 className={`text-xs font-semibold uppercase tracking-wide ${muted}`}>Account Details</h3>
                {isLocalBank ? (
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${muted}`}>Bank Name</label>
                      <input
                        type="text"
                        value={form.bankName}
                        onChange={handleFieldChange('bankName')}
                        placeholder="Enter bank name"
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${muted}`}>Account Title</label>
                      <input
                        type="text"
                        value={form.accountTitle}
                        placeholder="Account title"
                        className={disabledInputCls}
                        disabled
                        readOnly
                        aria-disabled="true"
                        tabIndex={-1}
                      />
                      <p className={`text-[11px] mt-1.5 ${muted}`}>Auto-filled from your login account</p>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${muted}`}>Account Number</label>
                      <input
                        type="text"
                        value={form.accountNumber}
                        onChange={handleFieldChange('accountNumber')}
                        placeholder="Enter account number"
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${muted}`}>Confirm Account Number</label>
                      <input
                        type="text"
                        value={form.confirmAccountNumber}
                        onChange={handleFieldChange('confirmAccountNumber')}
                        placeholder="Re-enter account number"
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${muted}`}>IBAN #</label>
                      <input
                        type="text"
                        value={form.iban}
                        onChange={handleFieldChange('iban')}
                        placeholder="Enter IBAN"
                        className={inputCls}
                      />
                    </div>
                  </div>
                ) : isBank ? (
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${muted}`}>Bank Name</label>
                      <input
                        type="text"
                        value={form.bankName}
                        onChange={handleFieldChange('bankName')}
                        placeholder="Enter bank name"
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${muted}`}>Account Title</label>
                      <input
                        type="text"
                        value={form.accountTitle}
                        placeholder="Account title"
                        className={disabledInputCls}
                        disabled
                        readOnly
                        aria-disabled="true"
                        tabIndex={-1}
                      />
                      <p className={`text-[11px] mt-1.5 ${muted}`}>Auto-filled from your login account</p>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${muted}`}>Account Number</label>
                      <input
                        type="text"
                        value={form.accountNumber}
                        onChange={handleFieldChange('accountNumber')}
                        placeholder="Enter account number"
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${muted}`}>IBAN #</label>
                      <input
                        type="text"
                        value={form.iban}
                        onChange={handleFieldChange('iban')}
                        placeholder="Enter IBAN"
                        className={inputCls}
                      />
                    </div>
                  </div>
                ) : needsCryptoAddress ? (
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${muted}`}>Account Title</label>
                      <input
                        type="text"
                        value={form.accountTitle}
                        placeholder="Account title"
                        className={disabledInputCls}
                        disabled
                        readOnly
                        aria-disabled="true"
                        tabIndex={-1}
                      />
                      <p className={`text-[11px] mt-1.5 ${muted}`}>Auto-filled from your login account</p>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${muted}`}>Wallet Address</label>
                      <p className={`text-[11px] -mt-1 mb-2 ${muted}`}>TRC20 address will be used</p>
                    <input
                      type="text"
                      value={form.cryptoAddress}
                      onChange={handleFieldChange('cryptoAddress')}
                      placeholder="Enter TRC20 wallet address"
                      className={inputCls}
                      required
                    />
                    </div>
                  </div>
                ) : (
                  <p className={`text-sm ${muted}`}>
                    Selected method: <span className={text}>{selectedWallet.title}</span>
                  </p>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={isSaving || isLoadingWallets || !selectedWallet}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
                style={{ backgroundColor: THEME }}
              >
                <Wallet className="w-4 h-4" />
                {isSaving ? 'Saving…' : editingId ? 'Update Account' : 'Save Account'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className={`px-6 py-3 rounded-xl text-sm font-semibold border ${
                  isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className={`rounded-2xl border shadow-sm ${card}`}>
            {isLoadingAccounts ? (
              <div className="flex justify-center py-14">
                <NewtonsCradleLoader />
              </div>
            ) : savedAccounts.length === 0 ? (
              <div className="text-center py-14 px-6">
                <div className={`w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <Wallet className={`w-6 h-6 ${muted}`} />
                </div>
                {loadError ? (
                  <>
                    <p className={`text-sm font-medium ${text}`}>Could not load withdrawal details</p>
                    <p className={`text-xs mt-1 mb-5 ${muted}`}>{loadError}</p>
                    <button
                      type="button"
                      onClick={loadSavedAccounts}
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white"
                      style={{ backgroundColor: THEME }}
                    >
                      Retry
                    </button>
                  </>
                ) : (
                  <>
                    <p className={`text-sm font-medium ${text}`}>No withdrawal details found</p>
                    <p className={`text-xs mt-1 mb-5 ${muted}`}>Your saved payment details from the server will appear here.</p>
                    <button
                      type="button"
                      onClick={openAddForm}
                      disabled={isLoadingWallets}
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
                      style={{ backgroundColor: THEME }}
                    >
                      <Plus className="w-4 h-4" />
                      Add Account
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {savedAccounts.map((account) => {
                  const isBankAccount = account.methodKey === 'bank';
                  const accountIconUrl =
                    resolveWalletIconUrl({
                      title: account.subPaymentMethodTitle || account.walletTitle,
                      value: account.raw?.accountCode,
                    }) || resolveWalletIconUrl({ title: account.walletTitle });
                  const detailRows = isBankAccount
                    ? [
                        { label: 'Bank Name', value: account.bankName },
                        { label: 'Account Title', value: account.accountTitle },
                        { label: 'Account Number', value: account.accountNumber },
                        { label: 'IBAN #', value: account.iban || account.swiftCode },
                      ]
                    : [
                        { label: 'Account Title', value: account.accountTitle },
                        { label: 'Payment Network', value: account.subPaymentMethodTitle || account.walletTitle },
                        { label: 'Wallet Address', hint: 'TRC20 address will be used', value: account.cryptoAddress },
                      ];

                  return (
                    <div key={account.id} className="p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isDarkMode ? 'bg-gray-800' : 'bg-red-50'}`}>
                            <WalletMethodIcon
                              iconUrl={accountIconUrl}
                              methodKey={account.methodKey}
                              imgClassName="w-6 h-6 object-contain"
                              iconClassName="w-5 h-5 text-red-800"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className={`text-sm font-semibold ${text}`}>{account.walletTitle}</p>
                            <p className={`text-xs mt-0.5 ${muted}`}>
                              {account.accountType || (isBankAccount ? 'Bank' : 'Crypto')}
                              {account.subPaymentMethodTitle && isBankAccount ? ` · ${account.subPaymentMethodTitle}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => openEditForm(account)}
                            className={`p-2 rounded-lg transition ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                            aria-label="Edit account"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className={`mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl p-4 ${isDarkMode ? 'bg-gray-800/40' : 'bg-gray-50'}`}>
                        {detailRows.map((row) => (
                          <div key={row.label}>
                            <p className={`text-[10px] uppercase tracking-wide ${muted}`}>{row.label}</p>
                            {row.hint ? (
                              <p className={`text-[10px] mt-0.5 normal-case tracking-normal ${muted}`}>{row.hint}</p>
                            ) : null}
                            <p className={`text-sm font-medium mt-0.5 break-all ${text}`}>{displayVal(row.value)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddWithdrawalDetails;
